import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { TimeRecordingService } from '../../services/time-recording/time-recording.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { CompositeModel } from '../complex/composite.model';
import { MaterialModel } from '../low-level/material.model';
import { CONSUME_CODE } from '../low-level/material.enum';
import { MeasurementToolModel } from '../low-level/measurement-tool.model';
import { MultilineTextModelOptions } from '../options/multiline-text-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { TextModel } from '../primitive/text.model';
import { ConfirmationEquipmentModel } from './confirmation-equipment.model';
import { ConfirmationModel } from './confirmation.model';
import { ParticipantModel } from './participant.model';
import { TIMERECORDING_TYPE_OF_WORK_CODES, TimeRecordingEntryModel } from './time-recording-entry.model';
import { TimeRecordingGeneralEntryModel } from './time-recording-general-entry.model';

export class ConfirmationSummaryModel extends CompositeModel {
    protected clientManager: ClientManagerInterface;

    public customerName: TextModel;
    public customerNotAvail: DisplayDecorator<BooleanModel>;
    public customerSignature: TextModel;
    public cseSignature: TextModel;
    public customerContact: TextModel;

    // dependencies
    private translate: TranslateService;
    private modelOptionsLoader: ModelOptionsLoaderService;
    private timeRecordingService: TimeRecordingService;
    private readonly configService: ConfigService;

    // not in outmodel, just for usage in confirmation-summary controller
    public recoveryDateTime: moment.Moment;
    public troubleText: MultilineTextModel | undefined;
    public reportCodes: string;
    public reportText: MultilineTextModel | undefined;
    public summaryMaterial: MaterialModel[];
    public summaryParticipants: ParticipantModel[];
    public timeRecordingTravelToDuration: moment.Duration;
    public timeRecordingTravelToEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingTravelToFirstEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingTravelToLastEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingOnsiteDuration: moment.Duration;
    public timeRecordingOnsiteFirstEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingOnsiteLastEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingTravelBackDuration: moment.Duration;
    public timeRecordingTravelBackEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingBreakDuration: moment.Duration | undefined;
    public timeRecordingWaitDuration: moment.Duration | undefined;
    public timeRecordingPhoneFixDuration: moment.Duration;
    public timeRecordingPhoneFixEntry: TimeRecordingGeneralEntryModel | undefined;
    public timeRecordingOnsiteEntriesList: TimeRecordingGeneralEntryModel[];
    public timeRecordingRemoteWorkFirstEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingRemoteWorkLastEntry: TimeRecordingEntryModel | undefined;
    public timeRecordingRemoteWorkDuration: moment.Duration | undefined;
    public readonly customerAgreementTextClause: string;
    public measurementTools: MeasurementToolModel[];
    public measurementToolsDetailsList: MeasurementToolModel[];
    public readonly additionalExpensesText: string;

    /**
     * Prepare a model for the Confirmation Summary Model.
     *
     * @param confirmationModel
     * @class
     */
    public constructor(public confirmationModel: ConfirmationModel) {
        super('ConfirmationSummaryModel', {});

        const injector = CustomInjector.getInjector();
        this.translate = injector.get(TranslateService);
        this.modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.timeRecordingService = injector.get(TimeRecordingService);
        this.configService = injector.get(ConfigService);
        const valueHelperService = injector.get(ValueHelperService);

        this.clientManager = injector.get(ClientManagerInstanceToken);

        if (this.clientManager.isNgmChina()) {
            let textClause = <MultilineTextModel>(
                this.set(
                    new MultilineTextModel(
                        'CrmLongTextTECLSet',
                        valueHelperService.getCustomerSignatureAgreementTextClause(),
                    ),
                )
            );
            textClause = assignModelOptions(
                textClause,
                {
                    ...textClause.options,
                    formatMultilineTextPattern: this.configService.getTextClausePattern(),
                },
                this,
            );
            this.customerAgreementTextClause = textClause.getFormattedText();
        } else {
            this.customerAgreementTextClause = valueHelperService.getCustomerSignatureAgreementTextClause();
        }
        // US does not have an equipment sections
        if (!this.clientManager.isPaceUS() && !this.confirmationModel.equipment) {
            throw new Error(
                `No equipment section in confirmation for notification ${this.confirmationModel.notificationIdentifier.toString()}!`,
            );
        }

        this.customerName = <TextModel>this.set(new TextModel('CustomerName'));
        this.modelOptionsLoader.load(this.customerName, 'ConfirmationSummary', 'CustomerName');

        const customerNotAvailableCheck = new DisplayDecorator(
            <string>this.translate.instant('CONFIRMATION_SUMMARY_CONTACT_PERSON_NOT_AVAILABLE_LABEL'),
            new BooleanModel('CustomerNotAvailable', false),
        );

        // For NGMAA Service Report we need to send back a flag to SAP to determine if Customer has signed or No
        this.customerNotAvail = this.clientManager.isNgmAA()
            ? <DisplayDecorator<BooleanModel>>this.set(customerNotAvailableCheck)
            : customerNotAvailableCheck;

        this.customerSignature = <TextModel>this.set(new TextModel('CustomerSignature'));
        if (
            (this.clientManager.isNgmAP0() && !this.clientManager.isNgmAAANZ()) ||
            (this.clientManager.isNgmP58() && this.configService.getDisplayCseSignature())
        ) {
            this.cseSignature = <TextModel>this.set(new TextModel('CSESignature'));
        }

        if (this.configService.getDisplayCustomerContact()) {
            this.customerContact = <TextModel>this.set(new TextModel('CustomerContact'));
            this.modelOptionsLoader.load(this.customerContact, 'ConfirmationSummary', 'CustomerContact');
        }

        // #### #########################################################################################################
        // Following section does not add something to the outmodel.
        // These values are mainly used for visualization in the markup of the confirmation-summary component.
        this.recoveryDateTime = this.getRecoveryDateTime(this.confirmationModel.equipment);

        // Use a MultilineTextModel here instead of simply binding the string in the HTML to conserve line breaks.
        // To prevent the user from editing it, we hard-code the readonly option.
        // Layout related options (rows) are aligned to the options used in the confirmation screen.
        if (this.clientManager.isNgmAAJapan() && this.confirmationModel.equipment) {
            this.troubleText = new MultilineTextModel(
                'TroubleText',
                this.confirmationModel.equipment.troubleTextLong.value,
                new MultilineTextModelOptions({ readonly: true, rows: 10 }),
            );
            this.troubleText = assignModelOptions(
                this.troubleText,
                {
                    ...this.troubleText.options,
                    columns: this.configService.getReportTextLineLength(),
                    lineLength: this.configService.getReportTextLineLength(),
                    formatMultilineTextPattern: this.configService.getFormatMultilineTextPattern(),
                },
                this,
            );
        }

        // Use a MultilineTextModel here instead of simply binding the string in the HTML to conserve line breaks.
        // To prevent the user from editing it, we hard-code the readonly option.
        // Layout related options (rows) are aligned to the options used in the confirmation screen.
        if (!this.clientManager.isPaceUS() && this.confirmationModel.equipment) {
            this.reportText = new MultilineTextModel(
                'ReportText',
                this.confirmationModel.equipment.reportTextLong.value,
                new MultilineTextModelOptions({ readonly: true, rows: 10 }),
            );
            this.reportText = assignModelOptions(
                this.reportText,
                {
                    ...this.reportText.options,
                    columns: this.configService.getReportTextLineLength(),
                    lineLength: this.configService.getReportTextLineLength(),
                    formatMultilineTextPattern: this.configService.getFormatMultilineTextPattern(),
                },
                this,
            );

            this.reportCodes = this.confirmationModel.equipment.reportCodes.selectedEntries
                .map((code) => code.displayName)
                .join(', ');
        }
        // MATERIAL and PARTICIPANTS
        this.doMaterialAndParticipantsProcessing();

        if (this.configService.getShowCalibrationDate()) {
            this.doCalibrationDateProcessing();
        }

        if (this.configService.getShowMeasurementToolsDetails()) {
            this.doMeasurementToolDetailsProcessing();
        }

        // BREAK & WAIT
        if (!this.clientManager.isPaceUS()) {
            this.timeRecordingWaitDuration = this.calculateWaitDuration(
                this.confirmationModel.timeRecording.timeRecordingEntries.value,
            );
            this.timeRecordingBreakDuration = this.calculateBreakDuration(
                this.confirmationModel.timeRecording.timeRecordingEntries.value,
            );

            // PHONE FIX, TRTO, TRBK, OSR, ROSR, FCC
            // These functions calculate the duration and set instance variables.
            if (this.confirmationModel.notificationCurrentStatus.isRemoteWork()) {
                this.doRemoteWorkProcessing();
            } else {
                this.doPhoneFixProcessing();
                this.doTravelToProcessing();
                this.doTravelBackProcessing();
                this.doOnsiteProcessing();
            }
        } else {
            // TODO: Do time recording processing for PACE US
        }
        if (
            this.configService.getDisplayAdditionalTravelCost() &&
            !this.confirmationModel.notificationCurrentStatus.isRemoteWork() &&
            this.confirmationModel.expenses?.additionalTravelExpenses.model.value
        ) {
            this.additionalExpensesText = this.configService.getAdditionalTravelCostText();
        }
    }

    public getOutModel(): ODataConfirmationSummaryOutModel {
        return <ODataConfirmationSummaryOutModel>super.getOutModel();
    }

    public isMaterialTaxiBillable(): boolean {
        if (this.confirmationModel.materials) {
            return this.confirmationModel.materials.listMaterial.value.some(
                (material: MaterialModel) => material.isMaterialConsumptionAdditionalTaxiCost,
            );
        }
        return false;
    }

    private doMaterialAndParticipantsProcessing(): void {
        this.summaryMaterial = this.confirmationModel.materials
            ? this.confirmationModel.materials.listMaterial.value.filter((material: MaterialModel) => {
                  return (
                      material.consumptionCode &&
                      material.consumptionCode.selectedEntry &&
                      ((this.clientManager.isNgmP58() &&
                          material.consumptionCode.selectedEntry.value === CONSUME_CODE.LEFT_ON_NOTIFICATION) ||
                          material.consumptionCode.selectedEntry.value === CONSUME_CODE.CONSUMED)
                  );
              })
            : [];

        this.summaryParticipants = this.confirmationModel.participants
            ? this.confirmationModel.participants.participantList.getSelectedParticipants()
            : [];
    }

    private doCalibrationDateProcessing(): void {
        this.measurementTools =
            this.confirmationModel.measurementTool && this.confirmationModel.measurementTool.measurementTools
                ? this.confirmationModel.measurementTool.measurementTools.value.filter(
                      (measTools: MeasurementToolModel) => measTools.measurementToolId.value !== '',
                  )
                : [];
    }

    public isRadioActiveToolUsed(): boolean {
        return this.confirmationModel.measurementTool
            ? this.confirmationModel.measurementTool.isRadioActiveSelectedYes &&
                  this.confirmationModel.measurementTool.radioActiveModelList.length > 0
            : false;
    }

    private doMeasurementToolDetailsProcessing(): void {
        this.measurementToolsDetailsList =
            this.confirmationModel.measurementTool &&
            !this.confirmationModel.measurementTool.noMeasurementTool.model.value &&
            this.confirmationModel.measurementTool.measurementTools
                ? this.confirmationModel.measurementTool.measurementTools.value
                : [];
    }
    private doPhoneFixProcessing(): void {
        const entries = <TimeRecordingGeneralEntryModel[]>(
            this.timeRecordingService.getEntriesByMultipleTypes(
                [TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX, TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES],
                this.confirmationModel.timeRecording.timeRecordingEntries.value,
            )
        );
        this.timeRecordingPhoneFixEntry = entries.length > 0 ? entries.pop() : undefined;
        this.timeRecordingPhoneFixDuration = this.calculateTotalDuration(
            this.timeRecordingPhoneFixEntry ? [this.timeRecordingPhoneFixEntry] : [],
        );
    }

    private doRemoteWorkProcessing(): void {
        const workCodes = this.confirmationModel.notificationCurrentStatus.isRemoteWorkOrTraining()
            ? [TIMERECORDING_TYPE_OF_WORK_CODES.ROSR, TIMERECORDING_TYPE_OF_WORK_CODES.ROSN]
            : [TIMERECORDING_TYPE_OF_WORK_CODES.FCC, TIMERECORDING_TYPE_OF_WORK_CODES.FCCN];
        const entries = <TimeRecordingGeneralEntryModel[]>(
            this.timeRecordingService.getEntriesByMultipleTypes(
                workCodes,
                this.confirmationModel.timeRecording.timeRecordingEntries.value,
            )
        );

        this.timeRecordingRemoteWorkFirstEntry = this.timeRecordingService.getFirstTimeRecordingEntry(entries);
        this.timeRecordingRemoteWorkLastEntry = this.timeRecordingService.getLastTimeRecordingEntry(entries);
        this.timeRecordingRemoteWorkDuration = this.calculateTotalDuration(entries);
    }

    private doTravelToProcessing(): void {
        if (this.configService.getEnableStandbyTravel()) {
            // If standby travel is enabled, there can be two travel-to entries in crm
            const entries = this.timeRecordingService.getTravelToEntries(
                this.confirmationModel.timeRecording.timeRecordingEntries.value,
            );
            this.timeRecordingTravelToFirstEntry = this.timeRecordingService.getFirstTimeRecordingEntry(entries);
            this.timeRecordingTravelToLastEntry = this.timeRecordingService.getLastTimeRecordingEntry(entries);
            this.timeRecordingTravelToDuration = this.calculateTotalDuration(entries);
        } else {
            const entries = this.timeRecordingService.getEntriesByType(
                TIMERECORDING_TYPE_OF_WORK_CODES.TRTO,
                this.confirmationModel.timeRecording.timeRecordingEntries.value,
            );
            this.timeRecordingTravelToEntry = entries.length > 0 ? entries.pop() : undefined;
            this.timeRecordingTravelToDuration = this.calculateTotalDuration(
                this.timeRecordingTravelToEntry ? [this.timeRecordingTravelToEntry] : [],
            );
        }
    }

    private doTravelBackProcessing(): void {
        const entries = this.timeRecordingService.getEntriesByType(
            TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
            this.confirmationModel.timeRecording.timeRecordingEntries.value,
        );
        this.timeRecordingTravelBackEntry = entries.length > 0 ? entries.pop() : undefined;
        this.timeRecordingTravelBackDuration = this.calculateTotalDuration(
            this.timeRecordingTravelBackEntry ? [this.timeRecordingTravelBackEntry] : [],
        );
    }

    private doOnsiteProcessing(): void {
        if (this.clientManager.isNgmAP0()) {
            this.doOnsiteProcessingAA();
        } else {
            this.doOnsiteProcessingP58();
        }
    }

    private doOnsiteProcessingP58(): void {
        const entries = this.timeRecordingService.getEntriesByType(
            TIMERECORDING_TYPE_OF_WORK_CODES.OSR,
            this.confirmationModel.timeRecording.timeRecordingEntries.value,
        );
        this.timeRecordingOnsiteFirstEntry = this.timeRecordingService.getFirstTimeRecordingEntry(entries);
        this.timeRecordingOnsiteLastEntry = this.timeRecordingService.getLastTimeRecordingEntry(entries);
        this.timeRecordingOnsiteDuration = this.calculateTotalDuration(entries);
    }

    private doOnsiteProcessingAA(): void {
        this.timeRecordingOnsiteEntriesList = this.timeRecordingService.getOSREntries(
            this.confirmationModel.timeRecording.timeRecordingEntries.value,
        );
    }

    private calculateWaitDuration(timeRecordingEntries: TimeRecordingEntryModel[]): moment.Duration | undefined {
        const entries = this.timeRecordingService.getEntriesByType(
            TIMERECORDING_TYPE_OF_WORK_CODES.WAIT,
            timeRecordingEntries,
        );
        if (entries.length > 0) {
            return this.calculateTotalDuration(entries);
        }
        return undefined;
    }

    private calculateBreakDuration(timeRecordingEntries: TimeRecordingEntryModel[]): moment.Duration | undefined {
        const entries = this.timeRecordingService.getEntriesByType(
            TIMERECORDING_TYPE_OF_WORK_CODES.BREAK,
            timeRecordingEntries,
        );
        if (entries.length > 0) {
            return this.calculateTotalDuration(entries);
        }
        return undefined;
    }

    // this function needs to be public, as it is being called in the markup of the confirmation-summary.component
    public calculateTotalDuration(entries: TimeRecordingEntryModel[]): moment.Duration {
        const totalDuration = moment.duration(0);
        if (entries && entries.length > 0) {
            entries.forEach((entry) => {
                if (entry) {
                    totalDuration.add(entry.endDateTime.diff(entry.startDateTime));
                }
            });
        }
        return totalDuration;
    }

    private getRecoveryDateTime(equipment: ConfirmationEquipmentModel | undefined): moment.Moment {
        if (equipment && equipment.timeOfRecoveryDate && equipment.timeOfRecoveryDate.value) {
            return equipment.timeOfRecoveryDate.value;
        }
        return moment.invalid();
    }
}

export interface ODataConfirmationSummaryOutModel extends ODataObject {
    CustomerName: string;
    CustomerSignature: string;
    CSESignature: string;
    CustomerContact: number;
    CrmLongTextTECLSet: ODataTextLineOutModel[];
}
